import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  Card,
  CardActionArea,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import LocalHospitalRoundedIcon from "@mui/icons-material/LocalHospitalRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AppContext, { AppContextType } from "../../core/context/auth-context";
import { IAppDetails } from "../../core/interface/general/IGeneral";
import {
  FHIR_ADMIN_PROVIDER_ID,
  exchangeProviderToken,
} from "../../core/service/provider-token-service";
import dearLogo from "../../assets/images/dear_logo.png";
import PasskeyManager from "../../components/passkey/passkey-manager";

type Accent = "primary" | "secondary";

interface ChoiceCardProps {
  accent: Accent;
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function ChoiceCard({ accent, icon, title, description, cta, busy, disabled, onClick }: ChoiceCardProps) {
  return (
    <Card
      sx={{
        flex: 1,
        transition: "border-color .18s ease, box-shadow .18s ease, transform .18s ease",
        "&:hover": disabled
          ? {}
          : {
              borderColor: `${accent}.main`,
              boxShadow: (t) => `0 10px 30px ${alpha(t.palette[accent].main, 0.16)}`,
              transform: "translateY(-3px)",
            },
      }}
    >
      <CardActionArea
        onClick={onClick}
        disabled={disabled}
        sx={{ height: "100%", p: 3 }}
      >
        <Stack sx={{ height: "100%", minHeight: 210 }} spacing={2} alignItems="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              display: "grid",
              placeItems: "center",
              color: `${accent}.contrastText`,
              bgcolor: `${accent}.main`,
            }}
          >
            {busy ? <CircularProgress size={28} color="inherit" /> : icon}
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{ mt: "auto", color: "text.primary", fontWeight: 600 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {busy ? "Connecting…" : cta}
            </Typography>
            <ArrowForwardRoundedIcon fontSize="small" />
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

const ConsoleChoice = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setAppContext } = useContext(AppContext) as AppContextType;
  const [connecting, setConnecting] = useState(false);

  // FHIR Admin console: exchange a token against the reserved "dearhealth" provider, then
  // land on the FHIR dashboard. This mints the adminToken up front so nothing 401s.
  const openFhirConsole = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const { adminToken, careTeams } = await exchangeProviderToken(FHIR_ADMIN_PROVIDER_ID);
      sessionStorage.setItem("providerName", "FHIR Admin");
      setAppContext((prev: IAppDetails) => ({
        ...prev,
        selectedProviderId: FHIR_ADMIN_PROVIDER_ID,
        authToken: adminToken,
        careTeams,
      }));
      navigate("/admin/fhir-server");
    } catch (error) {
      console.error("FHIR console token exchange failed:", error);
      enqueueSnackbar("Could not open the FHIR admin console. Please try again.", {
        variant: "error",
      });
      setConnecting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Stack alignItems="center" sx={{ minHeight: "100vh", justifyContent: "center", py: 6 }}>
        <Box component="img" src={dearLogo} alt="DEARhealth" sx={{ width: 200, mb: 4 }} />

        <Typography variant="h4" sx={{ mb: 0.5, textAlign: "center" }}>
          Where would you like to go?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: "center" }}>
          Choose a workspace to continue.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          sx={{ width: "100%" }}
          alignItems="stretch"
        >
          <ChoiceCard
            accent="primary"
            icon={<MonitorHeartRoundedIcon sx={{ fontSize: 32 }} />}
            title="FHIR Admin Console"
            description="Operational monitoring for the FHIR server — API access logs and notified-pull workflow tracking."
            cta="Open console"
            busy={connecting}
            disabled={connecting}
            onClick={openFhirConsole}
          />
          <ChoiceCard
            accent="secondary"
            icon={<LocalHospitalRoundedIcon sx={{ fontSize: 32 }} />}
            title="Provider Selection"
            description="Choose a provider to manage practitioners, care teams and push medical content."
            cta="Select a provider"
            disabled={connecting}
            onClick={() => navigate("/admin/providers")}
          />
        </Stack>

        {/* Passkey enrollment/management, offered right after sign-in (freshest Cognito session). */}
        <PasskeyManager />
      </Stack>
    </Container>
  );
};

export default ConsoleChoice;
